<template>
  <el-popover
    ref="popper"
    :popper-class="`search-popover ${customClass}`"
    trigger="click"
    :show-after="0"
    :hide-after="0"
    :show-arrow="false"
    @before-leave="resetDropdown"
  >
    <template #reference>
      <!-- Any content to trigger the popover goes in this slot -->
      <slot />
    </template>
    <div :class="`rounded py-3 px-3 ${customDropdownClass}`">
      <span class="flex items-center rounded w-full mb-3">
        <SearchBar
          data-intercom-target="Find text field"
          :search-text="searchInput"
          :placeholder="placeholderText"
          variant="no-border"
          custom-input-class="px-1 placeholder-gray-600 placeholder:[font-size:14px]"
          :show-clear-btn="false"
          @input="
            (newVal) => {
              searchInput = newVal;
            }
          "
        />
      </span>
      <span
        v-if="searchItems.length"
        ref="dropdownItemContainer"
        data-intercom-target="Saved Search dropdown"
        class="block max-h-[150px] overflow-y-auto"
      >
        <span v-for="(item, index) in searchItems" :key="index">
          <span
            class="w-full flex justify-between items-center text-gray-900 py-2 px-1 rounded hover:bg-secondary-50 cursor-pointer"
            :title="item.attributes.name"
            @click="handleItemClick(item)"
          >
            <span class="text-md flex flex-row items-center">
              <ClientOnly>
                <font-awesome-icon
                  v-if="getCustomIcon(item)"
                  class="mr-2"
                  :icon="getCustomIcon(item)"
                />
              </ClientOnly>
              <span
                class="w-[13.5rem] overflow-hidden whitespace-nowrap !text-ellipsis text-base"
                :class="{ 'font-semibold': item.id === selectedItem?.id }"
              >
                {{ item.attributes.name }}
              </span>
            </span>
            <ClientOnly v-if="savedSearchStore.onMentionsPageCheck">
              <font-awesome-icon
                class="text-lg"
                :icon="['far', 'pen-to-square']"
                @click.stop="handleClickEditSearch(item)"
              />
            </ClientOnly>
          </span>
          <span
            v-if="item.relationships.profiles.data.length > 1 && showProfiles"
          >
            <span
              v-for="profile in item.relationships.profiles.data"
              :key="profile.id"
              class="block cursor-pointer ml-8 bg-white hover:bg-accent-100 px-4 py-2 rounded-lg text-accent-800 font-normal text-ellipsis text-sm overflow-hidden leading-normal"
              :title="profile.attributes.name"
              @click="handleItemClick(profile)"
            >
              {{ profile.attributes.name }}
            </span>
          </span>
        </span>
      </span>
      <span v-else class="py-4 px-2 flex justify-center text-accent-400">
        {{ t("common.noResults") }}
      </span>
      <slot name="dropdown-footer" />
    </div>
  </el-popover>
</template>

<script setup lang="ts">
import { ref, Ref, computed } from "vue";
import { navigateTo } from "nuxt/app";
import { useI18n } from "vue-i18n";
import { ComparisonSets, ComparisonSetItem } from "~/types/SearchStateType";
import { useSearchStore } from "~/store/search";
import SearchBar from "~/components/shared/SearchBarInput.vue";

const { t } = useI18n();

const props = defineProps({
  placeholderText: {
    type: String,
    default: "Find",
  },
  dropdownItems: {
    type: Array as () => ComparisonSets,
    default: () => [],
  },
  customClass: {
    // Used for adding any custom tailwind css to the popover
    type: String,
    default: "",
  },
  customDropdownClass: {
    type: String,
    default: "w-full",
  },
  showProfiles: {
    type: Boolean,
    default: false,
  },
  selectedItem: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["selected"]);

const savedSearchStore = useSearchStore();

const searchInput: Ref = ref("");
const dropdownItemContainer: Ref = ref(null);
const popper: Ref = ref(null);

const searchItems = computed(() => {
  const filtered: ComparisonSets = props.dropdownItems.filter((item) =>
    item.attributes.name
      ?.toLowerCase()
      .includes(searchInput.value.toLowerCase()),
  );
  return filtered;
});

const resetDropdown = () => {
  searchInput.value = "";
  if (dropdownItemContainer.value) {
    dropdownItemContainer.value.scrollTop = 0;
  }
};

const handleItemClick = (item: ComparisonSetItem) => {
  savedSearchStore.isUntitledSearch = false;
  emit("selected", item);
  closePopover();
};

const closePopover = () => {
  popper.value.hide();
};

const getCustomIcon = (item: ComparisonSetItem) => {
  if (item.attributes.is_comparison) {
    return ["far", "fa-circles-overlap"];
  }
  return null;
};

const handleClickEditSearch = async (item: ComparisonSetItem) => {
  localStorage.setItem("searchToEdit", item.id);
  await navigateTo("/search");
};

defineExpose({
  closePopover,
});
</script>

<style>
@import url("https://fonts.googleapis.com/css?family=Inter:300,400,700&display=swap");

.search-popover {
  font-family: Inter, sans-serif;
  @apply !w-fit !p-0 !rounded !shadow-popover !z-10;
}
</style>
